import React, { useContext, useEffect, useState } from "react";
import { TextField, IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import styles from "./master-setup-client.module.css";
import { toast } from "react-toastify";
import { clientService } from "../../utils/services/clientService";
import { useNavigate } from "react-router-dom";
import AppContext from "../../components/context/context";
import { forgotPasswordService } from "../../utils/services/loginService.js";

function OnBoardLogin() {
  const { setIsAuthenticated, setUser } = useContext(AppContext);
  const [password, setPassword] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [email, setEmail] = useState("");
  const [validateKey, setValidateKey] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [iframeRedirectFailed, setIframeRedirectFailed] = useState(false);
  const navigate = useNavigate();
  const currentUrl = window.location.href;
  const isStrongPassword = (password) => {
    const strongPasswordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[\W_]).*$/;
    return strongPasswordRegex.test(password);
  };
  useEffect(() => {
    // Check if page is loaded in an iframe and break out
    const breakOutOfIframe = () => {
      try {
        if (window.self !== window.top) {
          // We're in an iframe, change the parent window URL
          const currentUrl = window.location.href;

          try {
            // Try to change the top window location directly
            window.top.location.href = currentUrl;
            return true;
          } catch (e) {
            // If direct access fails due to cross-origin restrictions, try alternatives
            try {
              // Try using parent window
              window.parent.location.href = currentUrl;
              return true;
            } catch (e2) {
              // Try postMessage to communicate with parent
              try {
                window.parent.postMessage(
                  {
                    type: "REDIRECT_PARENT",
                    url: currentUrl,
                  },
                  "*",
                );
                // Give the parent a short moment to act. If it doesn't, show the fallback
                setTimeout(() => setIframeRedirectFailed(true), 600);
                // If postMessage was sent, stop further automatic attempts and let the parent handle it
                return true;
              } catch (e3) {
                // If we've reached here, automatic redirect attempts failed (popup/block or cross-origin). Show in-app fallback UI
                setIframeRedirectFailed(true);
                return true;
              }
            }
          }
        }
        return false; // Not in iframe, continue normally
      } catch (e) {
        console.error("Error checking iframe status:", e);
        return false;
      }
    };

    // If we're breaking out of iframe, don't continue with normal initialization
    if (breakOutOfIframe()) {
      return;
    }

    // Normal page initialization
    const urlObj = new URL(currentUrl);
    const emailParam = urlObj.searchParams.get("email");
    const validate_key = urlObj.searchParams.get("validate_key");
    const otpSentParam = urlObj.searchParams.get("otpsent");

    setValidateKey(validate_key);
    setEmail(decodeURIComponent(emailParam));
    if (otpSentParam == "true") {
      setIsOtpSent(true);
    }
  }, [currentUrl]);
  console.log(isOtpSent);
  const handleSendOtp = async () => {
    try {
      setLoading(true);
      const response = await forgotPasswordService.sendOTP(email, true);
      if (response) {
        toast.success("OTP sent to your email!");
        setIsOtpSent(true);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error sending OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      setLoading(true);
      const response = await forgotPasswordService.validateOTP(email, otp);
      if (response) {
        toast.success("OTP verified successfully!");
        setIsOtpVerified(true);
      }
    } catch (error) {
      console.error(error);
      toast.error("Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const onSave = async () => {
    if (!password.newPassword || !password.confirmPassword) {
      toast.error("Please enter new password and confirm password", {
        autoClose: 3000,
      });
      return;
    } else if (password.newPassword !== password.confirmPassword) {
      toast.error("Passwords do not match", { autoClose: 3000 });
      return;
    } else if (!isStrongPassword(password.confirmPassword)) {
      toast.error(
        "Password must be at least 4 characters long and contain at least one lowercase letter, one uppercase letter, one number, and one special character!",
        {
          position: "bottom-left",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        },
      );
      return;
    } else {
      try {
        let response = await clientService.createPassword({
          email: email,
          password: password.newPassword,
          newPassword: password.newPassword,
          validate_key: validateKey,
        });
        if (response) {
          localStorage.setItem("token", response.accessToken);
          localStorage.setItem("user", JSON.stringify(response.user));
          localStorage.setItem("client_id", response.client?._id);
          localStorage.setItem(
            "roles",
            JSON.stringify(response.authentication?.payload?.roles),
          );
          setUser(response.user);
          toast.success("Logged in successfully");
          localStorage.setItem("path", `/account-setting`);
          setIsAuthenticated(true);
          navigate("/account-setting", { replace: true });
        }
      } catch (e) {
        console.error(e);
        console.error(e);
      }
    }
  };

  return (
    <main className={styles.masterSetupClient}>
      <div className={styles.container}>
        {iframeRedirectFailed && (
          <div style={{ padding: 16, textAlign: "center" }}>
            <h2>Redirect blocked</h2>
            <p>
              We were unable to automatically open this page in a new window.
              Click the link below to continue the onboarding process in the
              main window.
            </p>
            <button
              onClick={() => {
                try {
                  window.top.location.href = currentUrl;
                } catch (e) {
                  // As a last resort, open in the same window
                  window.location.href = currentUrl;
                }
              }}
              style={{
                background: "#1976d2",
                color: "#fff",
                padding: "8px 12px",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
              }}
            >
              Click here to continue onboarding
            </button>
          </div>
        )}
        {!iframeRedirectFailed && (
          <div className={styles.contentWrapper}>
            <section className={styles.imageColumn}>
              <img
                loading="lazy"
                src="/onboard.png"
                alt="Welcome illustration"
                className={styles.mainImage}
              />
            </section>
            <section className={styles.formColumn}>
              <div className={styles.formWrapper}>
                <h1 className={styles.welcomeTitle}>Welcome aboard</h1>
                <p className={styles.instructionText}>
                  Let's set up a personalized password for secured access to
                  your own DropSkip™️ portal.
                </p>
                <TextField
                  sx={{ width: "90%" }}
                  size="small"
                  margin="normal"
                  label="Email ID"
                  type="email"
                  value={email}
                  disabled
                />
                <TextField
                  size="small"
                  sx={{ width: "90%" }}
                  margin="normal"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={password.newPassword}
                  onChange={(e) =>
                    setPassword({ ...password, newPassword: e.target.value })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  size="small"
                  sx={{ width: "90%" }}
                  margin="normal"
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={password.confirmPassword}
                  onChange={(e) =>
                    setPassword({
                      ...password,
                      confirmPassword: e.target.value,
                    })
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <div onClick={onSave} className={styles.loginButton}>
                  Login
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    </main>
  );
}

export default OnBoardLogin;
